import React, { useEffect, useMemo, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { BottomNav } from "@/components/BottomNav";
import { CategoryChips } from "@/components/CategoryChips";
import { ProductCard } from "@/components/ProductCard";
import { EmptyState } from "@/components/EmptyState";
import { ProductGridSkeleton } from "@/components/Skeleton";
import { CompareBar } from "@/components/CompareBar";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { fetchProducts } from "@/lib/api";
import type { Product, ProductCategory } from "@/types";

type SortKey = "popular" | "priceAsc" | "priceDesc";

export function CatalogPage() {
  const { t, locale } = useI18n();
  const { route, navigate } = useRouter();
  const category = route.name === "catalog" ? route.category : undefined;
  const routeQuery = route.name === "catalog" ? route.q ?? "" : "";

  const [search, setSearch] = useState(routeQuery);
  // a search typed in the desktop header arrives through the address
  useEffect(() => setSearch(routeQuery), [routeQuery]);
  const [sort, setSort] = useState<SortKey>("popular");
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchProducts({ category, search: search || undefined })
      .then((list) => alive && setProducts(list))
      .catch(() => alive && setProducts([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [category, search, locale]);

  const sorted = useMemo(() => {
    const copy = [...products];
    if (sort === "priceAsc") copy.sort((a, b) => a.variants[0].priceUzs - b.variants[0].priceUzs);
    if (sort === "priceDesc") copy.sort((a, b) => b.variants[0].priceUzs - a.variants[0].priceUzs);
    return copy;
  }, [products, sort]);

  return (
    <>
      <TopBar title={t("catalog.title")} />
      <main className="page">
        <input
          style={{
            width: "100%",
            border: "1px solid var(--color-line)",
            borderRadius: "var(--radius-s)",
            padding: "12px 14px",
            fontSize: 15,
            background: "var(--color-surface)",
            marginBottom: "var(--space-4)",
          }}
          placeholder={t("catalog.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <CategoryChips value={category} onChange={(c) => navigate({ name: "catalog", category: c })} />

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "var(--space-3)" }}>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            style={{ border: "1px solid var(--color-line)", borderRadius: "var(--radius-s)", padding: "6px 10px", fontSize: 13, background: "var(--color-surface)" }}
          >
            <option value="popular">{t("catalog.sort.popular")}</option>
            <option value="priceAsc">{t("catalog.sort.priceAsc")}</option>
            <option value="priceDesc">{t("catalog.sort.priceDesc")}</option>
          </select>
        </div>

        {loading && products.length === 0 ? (
          <ProductGridSkeleton count={6} />
        ) : sorted.length === 0 ? (
          <EmptyState art="search" title={t("catalog.empty")} hint={search ? t("catalog.emptyHint") : undefined} />
        ) : (
          <div className="grid-2">
            {sorted.map((p) => (
              <ProductCard key={p.id} product={p} width="100%" />
            ))}
          </div>
        )}
      </main>
      <CompareBar raised />
      <BottomNav />
    </>
  );
}
